import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, AlertTriangle, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useDceAnalysis,
  useDceAnalysisHistoryDetail,
  useDceRequiredDocuments,
  useTriggerDceAnalysis,
} from '../../hooks/useDceAnalysis';
import type { DceAnalysisStatus } from '../../types/dce-analysis.types';
import { ConstraintsSection } from './ConstraintsSection';
import { CriteriaSection } from './CriteriaSection';
import { ProfilesSection } from './ProfilesSection';
import { RequirementsSection } from './RequirementsSection';

interface Props {
  opportunityId: number;
}

const STATUS_LABELS: Record<DceAnalysisStatus, string> = {
  PENDING: 'En attente',
  RUNNING: 'En cours…',
  DONE: 'Terminée',
  PARTIAL_SUCCESS: 'Terminée (partielle)',
  FAILED: 'En échec',
};

const STATUS_COLORS: Record<DceAnalysisStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-700 border-gray-300',
  RUNNING: 'bg-blue-100 text-blue-700 border-blue-300',
  DONE: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  PARTIAL_SUCCESS: 'bg-amber-100 text-amber-700 border-amber-300',
  FAILED: 'bg-red-100 text-red-700 border-red-300',
};

export function DceAnalysisTab({ opportunityId }: Props) {
  const { data: analysis, isLoading } = useDceAnalysis(opportunityId);
  const { data: requiredDocs = [] } = useDceRequiredDocuments(
    analysis?.status === 'DONE' || analysis?.status === 'PARTIAL_SUCCESS'
      ? opportunityId
      : null,
  );
  // Charge l'analyse précédente uniquement pour afficher son timestamp dans le bandeau.
  const { data: previousAnalysis } = useDceAnalysisHistoryDetail(
    analysis?.previousAnalysisId ? opportunityId : null,
    analysis?.previousAnalysisId ?? null,
  );
  const triggerMutation = useTriggerDceAnalysis(opportunityId);

  const handleRun = (force = false) => {
    triggerMutation.mutate(force, {
      onSuccess: (res) => {
        if (res.enqueued) {
          toast.success(
            res.reason === 'stuck-recovered'
              ? 'Analyse précédente bloquée libérée — nouvelle analyse lancée'
              : 'Analyse lancée — résultats sous 3-10 min',
          );
        } else {
          toast.info(
            "Une analyse récente existe déjà. Utiliser \"Forcer une relance\" pour la re-générer.",
          );
        }
      },
      onError: () => toast.error("Impossible de lancer l'analyse"),
    });
  };

  // Vue chargement initiale
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-copper" />
      </div>
    );
  }

  // Pas d'analyse encore lancée
  if (!analysis) {
    return (
      <div className="rounded-md border bg-card p-8 text-center">
        <h3 className="font-semibold mb-2">Aucune analyse encore réalisée</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Aucun DCE indexé n'est disponible pour cette opportunité.
          Importez et indexez un DCE pour déclencher automatiquement l'analyse,
          ou lancez-la manuellement si l'indexation est déjà terminée.
        </p>
        <Button
          onClick={() => handleRun(false)}
          disabled={triggerMutation.isPending}
        >
          {triggerMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Lancer l'analyse manuellement
        </Button>
      </div>
    );
  }

  const inProgress = analysis.status === 'PENDING' || analysis.status === 'RUNNING';
  const finished =
    analysis.status === 'DONE' || analysis.status === 'PARTIAL_SUCCESS';

  return (
    <div className="space-y-4">
      {/* Header bar : statut + compteurs + action relancer */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="outline" className={STATUS_COLORS[analysis.status]}>
            {STATUS_LABELS[analysis.status]}
          </Badge>
          {finished && analysis.counts && (
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{analysis.counts.requirements}</span> exigences
              {' · '}
              <span className="font-semibold text-foreground">{analysis.counts.profiles}</span> profils
              {' · '}
              <span className="font-semibold text-foreground">{analysis.counts.requiredDocuments}</span> pièces administratives
            </div>
          )}
          {finished && analysis.totalCostUsd > 0 && (
            <span className="text-xs text-muted-foreground">
              · Coût IA : ${analysis.totalCostUsd.toFixed(4)}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {finished && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRun(true)}
              disabled={triggerMutation.isPending}
            >
              {triggerMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              <RefreshCcw className="h-3.5 w-3.5 mr-1" />
              Forcer une relance
            </Button>
          )}
          {analysis.status === 'FAILED' && (
            <Button
              size="sm"
              onClick={() => handleRun(false)}
              disabled={triggerMutation.isPending}
            >
              {triggerMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              <RefreshCcw className="h-3.5 w-3.5 mr-1" />
              Relancer l'analyse
            </Button>
          )}
        </div>
      </div>

      {/* Sprint 4.E — Bandeau "remplace une analyse précédente" si applicable */}
      {analysis.previousAnalysisId && (
        <div className="rounded-md border bg-blue-50 border-blue-200 p-3 text-sm">
          <div className="flex items-start gap-2">
            <History className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-blue-900">
                Cette analyse remplace une analyse précédente
                {previousAnalysis &&
                  ` du ${new Date(previousAnalysis.createdAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}`}
              </p>
              {previousAnalysis?.counts && (
                <p className="text-xs text-blue-700 mt-1">
                  {previousAnalysis.counts.requirementsValidated +
                    previousAnalysis.counts.profilesValidated +
                    previousAnalysis.counts.requiredDocumentsValidated}{' '}
                  /{' '}
                  {previousAnalysis.counts.requirements +
                    previousAnalysis.counts.profiles +
                    previousAnalysis.counts.requiredDocuments}{' '}
                  items étaient validés
                  {analysis.migratedItemsCount > 0 &&
                    ` · ${analysis.migratedItemsCount} statut${analysis.migratedItemsCount > 1 ? 's' : ''} migré${analysis.migratedItemsCount > 1 ? 's' : ''} automatiquement`}
                </p>
              )}
              <Link
                to={`/tm/opportunities/${opportunityId}/analysis/history/${analysis.previousAnalysisId}`}
                className="text-xs text-blue-700 hover:underline mt-1 inline-block"
              >
                Voir l'historique →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Erreur PARTIAL_SUCCESS */}
      {analysis.status === 'PARTIAL_SUCCESS' && analysis.partialFailures.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Échec partiel de l'analyse</p>
              <p className="text-xs text-amber-700 mt-1">
                {analysis.partialFailures.map((f) => f.source).join(', ')} —
                vous pouvez relancer pour ré-essayer les extractions échouées.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Erreur FAILED */}
      {analysis.status === 'FAILED' && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">L'analyse a échoué</p>
              <p className="text-xs text-red-700 mt-1">{analysis.errorMessage ?? 'Erreur inconnue'}</p>
            </div>
          </div>
        </div>
      )}

      {/* In progress */}
      {inProgress && (
        <div className="rounded-md border bg-card p-8 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-copper mb-3" />
          <p className="font-medium">Analyse en cours…</p>
          <p className="text-sm text-muted-foreground mt-1">
            L'extraction des exigences, critères, profils et contraintes prend généralement 3 à 10 minutes
            selon la taille du DCE.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Vous pouvez fermer cet onglet — une notification s'affichera dès que c'est terminé.
          </p>
        </div>
      )}

      {/* Sections : ordre = ordre de consommation par le bid manager */}
      {finished && (
        <>
          <ConstraintsSection analysis={analysis} requiredDocuments={requiredDocs} />
          <CriteriaSection constraints={analysis.opportunityConstraints} />
          <ProfilesSection analysis={analysis} opportunityId={opportunityId} />
          <RequirementsSection opportunityId={opportunityId} />
        </>
      )}
    </div>
  );
}
